// src/components/MapViewer.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { ScatterplotLayer, GeoJsonLayer } from '@deck.gl/layers';
import MapLibreMap, { Source, Layer } from 'react-map-gl/maplibre';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { WebMercatorViewport, LinearInterpolator } from '@deck.gl/core';

import { MAP_STYLES } from './Maps.js';
import { PIXEL_SPREAD } from '../constants/Constants.js';
import useElementSize from '../hooks/useElementSize.js';
import { getSingleColor, getMixedColor, handleSharedPointClick, getSharedTooltip } from './MapSharedUtils.js';

const project = (lng, lat, worldSize) => {
  const x = (lng + 180) * (worldSize / 360);
  const sinLat = Math.sin(lat * Math.PI / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldSize;
  return { x, y };
};

const unproject = (x, y, worldSize) => {
  const lng = (x / worldSize) * 360 - 180;
  const n = Math.PI - 2 * Math.PI * (y / worldSize);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return [lng, lat];
};

const ReactMap = MapLibreMap?.default?.Map || MapLibreMap?.Map || MapLibreMap?.default || MapLibreMap;

const resolveMapStyle = (styleConfig) => {
  if (!styleConfig) return "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
  let url = styleConfig.url;
  if (url.includes('.json') || url.includes('protocol')) return url;
  const cleanedUrl = url.replace('{s}', 'a').replace('{r}', '');
  return {
    version: 8,
    sources: { 'raster-tiles': { type: 'raster', tiles: [cleanedUrl], tileSize: 256, zoomOffset: -1, attribution: styleConfig.attribution || '' } },
    layers: [{ id: 'simple-tiles', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 22 }]
  };
};

const MapViewer = React.memo(function MapViewer({ 
  id, viewState, onViewStateChange, onBoundsChange = () => {}, processedData, 
  geojsonFeatures, colorBy, stats, stableColorConfig, isGradient, baseLayer,
  radiusMinPixels, radiusMaxPixels, lineWidth, disableTooltip = false, onSampleClick, shouldBroadcast = false, fallbackLayers = [],
  selectedSampleIndex,
  overlayRasterUrl,
  controller = true
}) {
  const [containerRef, { width, height }] = useElementSize({ debounceMs: 50 });
  const [expandedId, setExpandedId] = useState(null);
  const debounce = true;
  const [deckKey, setDeckKey] = useState(0); 
  const glContextRef = useRef(null);
  const errorCountRef = useRef(0);
  const rasterTiles = useMemo(() => overlayRasterUrl ? [overlayRasterUrl] : undefined, [overlayRasterUrl]);


  // Forcefully release the WebGL context on unmount or remount
  useEffect(() => {
    return () => {
      if (glContextRef.current) {
        const ext = glContextRef.current.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
        glContextRef.current = null;
      }
    };
  }, [deckKey, id]);

  const viewStateRef = useRef(viewState);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const prevSampleIndexRef = useRef(null);

  // Auto-Center on selected sample if it is off-screen
  useEffect(() => {
    if (selectedSampleIndex == null) {
      prevSampleIndexRef.current = null;
      return;
    }

    if (prevSampleIndexRef.current === selectedSampleIndex) {
      return;
    }

    let targetPoint = null;
    for (const group of processedData) {
      if (group.allSamples.some(s => s.index === selectedSampleIndex)) {
        targetPoint = group.position;
        break;
      }
    }

    if (targetPoint) {
      prevSampleIndexRef.current = selectedSampleIndex;

      const [lng, lat] = targetPoint;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

      const currentVS = viewStateRef.current;
      
      const visibleDeg = 360 / Math.pow(2, currentVS.zoom);
      const halfDeg = (visibleDeg / 2) * 0.8; 
      
      let dLng = Math.abs(lng - currentVS.longitude);
      if (dLng > 180) dLng = 360 - dLng;
      const dLat = Math.abs(lat - currentVS.latitude);
      
      if (dLng > halfDeg || dLat > halfDeg) {
        onViewStateChange({
          ...currentVS,
          longitude: lng,
          latitude: lat,
          zoom: currentVS.zoom,
          transitionDuration: 800,
          transitionInterpolator: new LinearInterpolator(['longitude', 'latitude'])
        });
      }
    }
  }, [selectedSampleIndex, processedData, onViewStateChange]);

  // --- LAYER FALLBACK LOGIC ---
  const [layerIndex, setLayerIndex] = useState(0);
  
  // Combine primary baseLayer and fallbacks into a single active array
  const activeLayers = useMemo(() => [baseLayer, ...fallbackLayers], [baseLayer, fallbackLayers.join(',')]);
  const currentBaseLayer = activeLayers[layerIndex] || baseLayer;

  const currentMapStyle = useMemo(
    () => resolveMapStyle(MAP_STYLES[currentBaseLayer]),
    [currentBaseLayer]
  );

  // Reset sequence if the parent explicitly requests a new base layer
  useEffect(() => {
    setLayerIndex(0);
  }, [baseLayer]);

  const handleMapError = (e) => {
    // Check if maplibre threw a load error (like 403 Forbidden for tiles)
    if (e && e.type === 'error') {
      setLayerIndex(prev => {
        // If we haven't reached the end of our fallback list, switch to the next one
        if (prev < activeLayers.length - 1) {
          console.warn(`[MapViewer] Failed to load map layer '${activeLayers[prev]}'. Falling back to '${activeLayers[prev + 1]}'.`);
          return prev + 1;
        }
        return prev;
      });
    }
  };
  // ----------------------------

  // Update bounds whenever viewState or dimensions change
  useEffect(() => {

    if (debounce && shouldBroadcast) {
    // --- DEBOUNCE LOGIC ---
    const timer = setTimeout(() => {
      const viewport = new WebMercatorViewport({ width, height, ...viewState });
      onBoundsChange(viewport.getBounds());
    }, 50); 

    return () => clearTimeout(timer);
    } else {

    if (width > 0 && height > 0 && shouldBroadcast) {
      const viewport = new WebMercatorViewport({ width, height, ...viewState });
      const bounds = viewport.getBounds(); // [minLng, minLat, maxLng, maxLat]
      onBoundsChange(bounds);
    }}
  }, [viewState, width, height, onBoundsChange, shouldBroadcast]);

  const { binaryAttributes, lookupData } = useMemo(() => {
    const allPoints = [];
    processedData.forEach(point => {
      const pointId = point.id || `${point.position[0]}-${point.position[1]}`;
      const isExpandedCenter = expandedId === pointId && point.count > 1;
      const values = point.allValues || (point.allSamples || []).map(s => s.value);
      const mixedColor = getMixedColor(values, isGradient, stats, stableColorConfig);
      
      allPoints.push({ ...point, pointId, isCenter: true, mixedColor });
      
      if (isExpandedCenter) {
        const safeZoom = Math.max(2.5, Math.min(Number.isFinite(viewState.zoom) ? viewState.zoom : 0, 8));
        const safeWorldSize = 256 * Math.pow(2, safeZoom);
        const centerPx = project(point.position[0], point.position[1], safeWorldSize);
        
        point.allSamples.forEach((s, childIdx) => {
          const angle = (childIdx / point.count) * 2 * Math.PI;
          const newPos = unproject(centerPx.x + Math.cos(angle) * PIXEL_SPREAD, centerPx.y + Math.sin(angle) * PIXEL_SPREAD, safeWorldSize);
          const childColor = getSingleColor(s.value, isGradient, stats, stableColorConfig);
          allPoints.push({
            ...point, isChild: true, displayVal: s.value, sampleIndex: s.index, position: newPos, isCenter: false, mixedColor: childColor
          });
        });
      }
    });

    const normalIndices = [];
    const selectedIndices = [];
    
    for (let i = 0; i < allPoints.length; i++) {
      const d = allPoints[i];
      const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
      
      let isSelected = false;
      if (selectedSampleIndex != null) {
        if (d.isChild) {
          isSelected = d.sampleIndex === selectedSampleIndex;
        } else if (!isExpandedCenter) {
          isSelected = d.allSamples.some(s => s.index === selectedSampleIndex);
        }
      }

      if (isSelected) {
        selectedIndices.push(i);
      } else {
        normalIndices.push(i);
      }
    }

    const visibleIndices = [...normalIndices, ...selectedIndices, ...selectedIndices];
    const n = visibleIndices.length;
    
    const positions = new Float32Array(n * 2);
    const fillColors = new Uint8Array(n * 4);
    const lineColors = new Uint8Array(n * 4);
    const radii = new Float32Array(n);
    const lw = new Float32Array(n);
    const lookup = new Array(n);

    const numNormal = normalIndices.length;
    const numSelected = selectedIndices.length;

    for (let i = 0; i < n; i++) {
      const d = allPoints[visibleIndices[i]];
      positions[i * 2] = d.position[0];
      positions[i * 2 + 1] = d.position[1];
      
      const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
      const fCol = isExpandedCenter ? [255, 255, 255, 255] : d.mixedColor;
      
      fillColors.set(fCol, i * 4);
      
      if (i < numNormal) {
        const actualLineWidth = lineWidth || 0;
        if (actualLineWidth === 0) {
          lineColors.set([fCol[0], fCol[1], fCol[2], 0], i * 4);
        } else {
          lineColors.set(isExpandedCenter ? [0, 0, 0, 255] : [255, 255, 255, 200], i * 4);
        }
        lw[i] = actualLineWidth;
        radii[i] = d.isChild ? 3 : Math.sqrt(d.count || 1);
      } else if (i < numNormal + numSelected) {
        lineColors.set([255, 255, 255, 255], i * 4);
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3) + 3;
        radii[i] = (d.isChild ? 3 : Math.sqrt(d.count || 1)) + 2;
      } else {
        lineColors.set([245, 73, 39, 255], i * 4);
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3);
        radii[i] = (d.isChild ? 3 : Math.sqrt(d.count || 1)) + 2;
      }
      lookup[i] = d;
    }

    return {
      binaryAttributes: {
        instancePositions: { value: positions, size: 2 },
        instanceFillColors: { value: fillColors, size: 4, normalized: true },
        instanceLineColors: { value: lineColors, size: 4, normalized: true },
        instanceLineWidths: { value: lw, size: 1 },
        instanceRadius: { value: radii, size: 1 }
      }, lookupData: lookup
    };
  }, [processedData, expandedId, isGradient, stats, stableColorConfig, (expandedId ? viewState.zoom : null), selectedSampleIndex, lineWidth]); 

  const layers = useMemo(() => [
    new ScatterplotLayer({
      id: `points-${id}`,
      data: { length: lookupData.length, attributes: binaryAttributes },
      pickable: true, stroked: true, radiusUnits: 'pixels',
      lineWidthUnits: 'pixels', getLineWidth: lineWidth || 1,
      lineWidthMinPixels: 0, lineWidthMaxPixels: 10, radiusMaxPixels, radiusMinPixels,
      onClick: (info) => handleSharedPointClick(info, lookupData, onSampleClick, setExpandedId),
      updateTriggers: { attributes: [binaryAttributes] }
    }),
    new GeoJsonLayer({
      id: `geojson-${id}`,
      data: (geojsonFeatures?.length > 0) ? { type: 'FeatureCollection', features: geojsonFeatures } : null,
      
      // Force the stroke to be visible in screen pixels, not geographic meters
      stroked: true,
      filled: true,
      lineWidthUnits: 'pixels',
      getLineWidth: lineWidth || 1, 
      
      // Make the fill semi-transparent (40 out of 255)
      getFillColor: f => {
        const c = getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig);
        return c ? [c[0], c[1], c[2], 70] : [239, 68, 68, 40];
      },
      // Keep the outline solid (255)
      getLineColor: f => {
        const c = getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig);
        return c ? [c[0], c[1], c[2], 255] : [239, 68, 68, 255];
      },
      
      pickable: true,
      updateTriggers: { 
        getFillColor: [colorBy, isGradient, stats, stableColorConfig], 
        getLineColor: [colorBy, isGradient, stats, stableColorConfig] 
      }
    })
  ], [id, lookupData.length, binaryAttributes, lineWidth, radiusMaxPixels, radiusMinPixels, geojsonFeatures, colorBy, isGradient, stats, stableColorConfig]);

  return (
    <div ref={containerRef} className="flex-grow w-full h-full relative overflow-hidden bg-slate-900">
      <DeckGL
        key={`deck-map-${id}-${deckKey}`}
        onWebGLInitialized={(gl) => {
          glContextRef.current = gl;
          errorCountRef.current = 0;
        }}
        onContextLost={(e) => {
          e.preventDefault();
          console.warn(`[MapViewer ${id}] Context lost. Attempting restore...`);
          if (errorCountRef.current < 5) {
            errorCountRef.current++;
            setTimeout(() => setDeckKey(k => k + 1), 250);
          }
        }}
        onError={(error) => {
          console.error(`[MapViewer ${id}] DeckGL Error:`, error);
          if (error && error.message && (error.message.toLowerCase().includes('webgl') || error.message.toLowerCase().includes('context'))) {
            if (errorCountRef.current < 5) {
              errorCountRef.current++;
              setTimeout(() => setDeckKey(k => k + 1), 250);
            }
          }
        }}
        viewState={viewState}
        onViewStateChange={e => onViewStateChange(e.viewState)}
        controller={controller} layers={layers}
        getTooltip={disableTooltip ? null : (info) => getSharedTooltip(info, lookupData, expandedId, colorBy)} 
      >
       <ReactMap mapLib={maplibregl} mapStyle={currentMapStyle} attributionControl={false} onError={handleMapError}>
          {overlayRasterUrl && (
            <Source id={`raster-source-${id}`} type="raster" tiles={rasterTiles} tileSize={256}>
              <Layer id={`raster-layer-${id}`} type="raster" paint={{ 'raster-opacity': 1.0, 'raster-contrast': 0.2 }} />
            </Source>
          )}
        </ReactMap>
      </DeckGL>
    </div>
  );
});

export default MapViewer;